import api from '../utils/api';

const BASE = '/api/feedback';

/**
 * Get all feedback for a classroom visible to a teacher (all threads whose assignment belongs to the classroom).
 * @route GET /api/feedback/teacher/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object[]>} Fully populated feedback threads
 */
export const getTeacherFeedback = (classroomId) =>
    api.get(`${BASE}/teacher/${classroomId}`).then((r) => r.data);

/**
 * Get the authenticated student's feedback threads for a classroom.
 * @route GET /api/feedback/student/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object[]>} Fully populated feedback threads sent by the current user
 */
export const getStudentFeedback = (classroomId) =>
    api.get(`${BASE}/student/${classroomId}`).then((r) => r.data);

/**
 * Get all feedback threads where the user is the sender or recipient.
 * @route GET /api/feedback/:userId
 * @param {string} userId
 * @returns {Promise<Object[]>} Feedback threads (other party and messages populated)
 */
export const getFeedback = (userId) =>
    api.get(`${BASE}/${userId}`).then((r) => r.data);

/**
 * Open a new feedback thread.
 * @route POST /api/feedback
 * @param {Object} data
 * @param {string} [data.sender] - sender User ObjectId (omit for anonymous)
 * @param {string} data.recipient - recipient User ObjectId
 * @param {string} [data.text]
 * @param {string} [data.situationOrigin]
 * @param {string} [data.scenarioOrigin]
 * @param {string} [data.assignmentId]
 * @returns {Promise<Object>} The created feedback document
 */
export const createFeedback = (data) =>
    api.post(BASE, data).then((r) => r.data);

/**
 * Append a message to an existing feedback thread.
 * @route PATCH /api/feedback/:feedbackId
 * @param {string} feedbackId
 * @param {{ message: { sender: string, text: string } }} data
 * @returns {Promise<Object>} The updated, fully populated feedback thread
 */
export const updateFeedback = (feedbackId, data) =>
    api.patch(`${BASE}/${feedbackId}`, data).then((r) => r.data);

/**
 * Close a feedback thread and remove it from the user's feedback list.
 * @route PATCH /api/feedback/closeFeedback/:feedbackId
 * @param {string} feedbackId
 * @param {'sent'|'received'} [type='received'] - which user array to pull from
 * @returns {Promise<string>} Confirmation message (plain text)
 */
export const closeFeedback = (feedbackId, type) =>
    api.patch(`${BASE}/closeFeedback/${feedbackId}`, { type }).then((r) => r.data);

/**
 * Delete a feedback thread and remove it from the user's feedback list.
 * @route DELETE /api/feedback/:feedbackId
 * @param {string} feedbackId
 * @param {'sent'|'received'} [type='received'] - which user array to pull from
 * @returns {Promise<string>} Confirmation message (plain text)
 */
export const deleteFeedback = (feedbackId, type) =>
    api.delete(`${BASE}/${feedbackId}`, { data: { type } }).then((r) => r.data);
